/**
 * src/modules/_kompozytor.ts — wspólne pole odpowiedzi (S-160, 10.10.2026, właściciel: „jedno wspólne okno”).
 *
 * Odpowiedź klientowi albo współpracownikowi pisze się w ośmiu miejscach: Wiadomości, Obsługa klienta, czat
 * zwrotu, czat dyskusji/reklamacji, Wiadomości PrestaShop, odpowiedź eBay, czat w karcie zamówienia,
 * komunikator zespołu. Każde miało własną obsługę Enter i wysokości pola — na telefonie Enter wysyłał
 * niedokończoną odpowiedź, w jednych polach pole rosło, w innych nie. Ten moduł to JEDNO zachowanie:
 *
 *  - telefon/tablet (pointer:coarse): Enter = zawsze nowa linia, wysyłka tylko przyciskiem;
 *  - laptop: Ctrl/⌘+Enter = zawsze wysyła; sam Enter zależy od miejsca (`skrot`):
 *      'enter'      — Enter wysyła, Shift+Enter nowa linia (Wiadomości, czaty — jak było);
 *      'ctrl-enter' — Enter = nowa linia (dyskusje, Obsługa klienta, eBay — dłuższe odpowiedzi, jak było);
 *  - pisanie przez IME (składanie znaków) nigdy nie wysyła;
 *  - pole rośnie z tekstem do `maksWys`, potem przewija się w środku;
 *  - klasa `sb-kompozytor` — wspólny wygląd na telefonie (mobile.css „20.”).
 *
 * Wysyłka zostaje w module danego miejsca (różne API, załączniki, blokady podwójnej wysyłki) — kompozytor
 * tylko woła `wyslij`. Ponowne podpięcie tego samego pola (moduły przerysowują pasek) podmienia opcje
 * zamiast dokładać drugi nasłuch.
 */

export type KompozytorOpcje = {
  /** Wywołanie wysyłki (np. klik przycisku „Wyślij” albo funkcja wysyłki modułu). */
  wyslij: () => void | Promise<void>;
  /** Laptop: czy sam Enter wysyła ('enter', domyślnie) czy tylko Ctrl/⌘+Enter ('ctrl-enter'). */
  skrot?: 'enter' | 'ctrl-enter';
  /** Wysokość pola w px: minimalna (domyślnie bez wymuszania) i maksymalna (domyślnie 160). */
  minWys?: number;
  maksWys?: number;
};

const KLUCZ = '_sbKompozytor';

export function naDotyku(): boolean {
  return typeof window !== 'undefined' && typeof window.matchMedia === 'function'
    && window.matchMedia('(pointer: coarse)').matches;
}

/** Czy to naciśnięcie klawisza ma wysłać wiadomość (czysta funkcja — test w tests/frontend). */
export function czyWyslac(e: { key: string; shiftKey?: boolean; altKey?: boolean; ctrlKey?: boolean; metaKey?: boolean; isComposing?: boolean; keyCode?: number; repeat?: boolean },
  skrot: 'enter' | 'ctrl-enter', dotyk: boolean): boolean {
  // Przytrzymany Enter (autopowtarzanie klawisza) nigdy nie wysyła kolejnych kopii (przegląd przed PROD, K-1).
  if (e.key !== 'Enter' || e.isComposing || e.keyCode === 229 || e.repeat) return false;
  if (dotyk) return false;
  if (e.ctrlKey || e.metaKey) return true;
  if (skrot === 'ctrl-enter') return false;
  return !e.shiftKey && !e.altKey;
}

/** Dopasowuje wysokość pola do treści (po pisaniu, wklejeniu, wstawieniu szablonu). */
export function dopasujWysokosc(pole: HTMLTextAreaElement | null): void {
  if (!pole) return;
  const o: KompozytorOpcje | undefined = (pole as any)[KLUCZ];
  const maks = (o && o.maksWys) || 160;
  const min = (o && o.minWys) || 0;
  pole.style.height = 'auto';
  pole.style.height = Math.min(maks, Math.max(min, pole.scrollHeight + 2)) + 'px';
}

/** Czyści pole po wysłaniu i przywraca wysokość startową. */
export function wyczyscKompozytor(pole: HTMLTextAreaElement | null): void {
  if (!pole) return;
  pole.value = '';
  pole.style.height = '';
}

export function podepnijKompozytor(pole: HTMLTextAreaElement | null, opcje: KompozytorOpcje): void {
  if (!pole) return;
  const juz = !!(pole as any)[KLUCZ];
  (pole as any)[KLUCZ] = opcje;
  if (!_wDymku(pole)) _ostatnie = pole;  // S-176: najświeżej podpięte pole odpowiedzi = cel „Wstaw” z dymka asystenta
  if (juz) return;
  pole.classList.add('sb-kompozytor');
  pole.addEventListener('focus', () => { if (!_wDymku(pole)) _ostatnie = pole; });
  // Klawiatura telefonu podpisuje klawisz „Enter” zgodnie z tym, co robi (nowa linia, nie „Wyślij”).
  pole.setAttribute('enterkeyhint', naDotyku() ? 'enter' : 'send');
  pole.addEventListener('keydown', (e: KeyboardEvent) => {
    const o: KompozytorOpcje = (pole as any)[KLUCZ];
    if (!o || !czyWyslac(e, o.skrot || 'enter', naDotyku())) return;
    e.preventDefault();
    void o.wyslij();
  });
  pole.addEventListener('input', () => dopasujWysokosc(pole));
}

// ── S-176: cel wstawiania tekstu z dymka asystenta ──
// Pola odpowiedzi do KUPUJĄCEGO (wątek, czat reklamacji, Obsługa klienta, czat zamówienia, czat zwrotu).
// Pola dymków (komunikator zespołu, sam asystent) nigdy nie są celem — tam nie pisze się do klienta.
let _ostatnie: HTMLTextAreaElement | null = null;
const _POLA_KUPUJACEGO = ['.ic-text-input', '#msg-reply-input', '.cs-msg-input', '#oc-chat-input', '#rd-chat-input', '#issue-reply-text'];

function _wDymku(pole: Element): boolean {
  return !!(pole.closest && pole.closest('#commdock-root, #asdock-root'));
}

/** Przegląd 13.10: zamknięte szuflady SellBase nie znikają przez display:none — zjeżdżają za ekran (transform)
 * albo dostają visibility:hidden, a offsetParent zostaje. Pole jest widoczne, gdy ma rozmiar, nie jest ukryte
 * stylem i leży w poziomie w oknie (pionowo może być przewinięte w dół szuflady). */
function _widoczne(pole: HTMLTextAreaElement | null): pole is HTMLTextAreaElement {
  if (!pole || !pole.isConnected || pole.offsetParent === null || pole.disabled || _wDymku(pole)) return false;
  try {
    if (getComputedStyle(pole).visibility === 'hidden') return false;
    const r = pole.getBoundingClientRect();
    const szer = window.innerWidth || document.documentElement.clientWidth || 0;
    if (r.width < 2 || r.height < 2) return false;
    if (szer && (r.left >= szer - 1 || r.right <= 1)) return false;
  } catch (_) { /* bez layoutu (testy) — zostają warunki podstawowe */ }
  return true;
}

/** Pole odpowiedzi do kupującego, do którego wstawić tekst: ostatnio używane (jeśli widoczne), inaczej pierwsze widoczne znane. */
export function aktywnyKompozytor(): HTMLTextAreaElement | null {
  if (_widoczne(_ostatnie)) return _ostatnie;
  for (const sel of _POLA_KUPUJACEGO) {
    const kandydaci = Array.from(document.querySelectorAll<HTMLTextAreaElement>(sel));
    const widoczny = kandydaci.find((p) => _widoczne(p));
    if (widoczny) return widoczny;
  }
  return null;
}

/** Pole odpowiedzi WEWNĄTRZ widoku sprawy (kontener z kontekstu dymka) — nigdy pole innej, schowanej rozmowy. */
export function kompozytorWKontenerze(kontener: Element | null | undefined): HTMLTextAreaElement | null {
  if (!kontener || !(kontener as any).querySelectorAll) return null;
  if (_ostatnie && kontener.contains(_ostatnie) && _widoczne(_ostatnie)) return _ostatnie;
  for (const sel of _POLA_KUPUJACEGO) {
    const widoczny = Array.from(kontener.querySelectorAll<HTMLTextAreaElement>(sel)).find((p) => _widoczne(p));
    if (widoczny) return widoczny;
  }
  return null;
}

/** Dokleja tekst (po pustej linii, gdy pole nie jest puste); zwraca false, gdy nie ma otwartego pola odpowiedzi.
 * Z `kontener` (widok sprawy, dla której jest podpowiedź) szuka pola tylko w nim. */
export function wstawDoAktywnegoKompozytora(tekst: string, kontener?: Element | null): boolean {
  const pole = kontener ? kompozytorWKontenerze(kontener) : aktywnyKompozytor();
  if (!pole) return false;
  const obecny = String(pole.value || '').replace(/\s+$/, '');
  pole.value = obecny ? obecny + '\n\n' + String(tekst || '') : String(tekst || '');
  pole.dispatchEvent(new Event('input', { bubbles: true }));
  dopasujWysokosc(pole);
  try { pole.focus(); pole.setSelectionRange(pole.value.length, pole.value.length); } catch (_) { /* nic */ }
  return true;
}

if (typeof window !== 'undefined') {
  (window as any).sbKompozytor = { podepnijKompozytor, dopasujWysokosc, wyczyscKompozytor, wstawDoAktywnegoKompozytora };
}
